import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpenIcon, HandThumbUpIcon, HandThumbDownIcon, LifebuoyIcon } from '@heroicons/react/24/outline';
import db, { collection, query, where, orderBy, limit, getDocs } from '../../firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { useHelpCentre } from '../../contexts/HelpCentreContext';
import { useHelpArticles } from '../../hooks/useHelpArticles';
import { pickLang } from '../../utils/helpCentre';
import HelpMarkdown from './HelpMarkdown';

const PAGE = 50;

function fmtDate(ts, lang) {
  const d = ts?.toDate?.();
  if (!d) return '';
  return d.toLocaleString(lang === 'es' ? 'es-ES' : 'en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

// Questions asked in the Help Centre. Everyone sees their own; an academy
// admin can switch to the whole academy's. Each query filters on the field
// firestore.rules' helpQuestions read rule checks (uid / academyId).
export default function HelpHistory({ onNavigate }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('es') ? 'es' : 'en';
  const { user, academyId, isAcademyAdmin } = useAuth();
  const { openArticle } = useHelpCentre();
  const { articles } = useHelpArticles();
  const [scope, setScope] = useState(isAcademyAdmin ? 'academy' : 'mine');
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    if (!user) return;
    let live = true;
    setRows(null);
    setError('');
    const q = scope === 'academy' && academyId
      ? query(collection(db, 'helpQuestions'), where('academyId', '==', academyId), orderBy('createdAt', 'desc'), limit(PAGE))
      : query(collection(db, 'helpQuestions'), where('uid', '==', user.uid), orderBy('createdAt', 'desc'), limit(PAGE));
    getDocs(q)
      .then((snap) => { if (live) setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() }))); })
      .catch((err) => { if (live) setError(err.message || 'Could not load questions.'); });
    return () => { live = false; };
  }, [scope, user, academyId]);

  const titleOf = (id) => pickLang(articles.find((a) => a.id === id)?.title, lang);

  return (
    <div className="space-y-3">
      {isAcademyAdmin && (
        <div className="inline-flex rounded-lg border border-gray-200 p-0.5 text-xs">
          {['academy', 'mine'].map((s) => (
            <button
              key={s} type="button" onClick={() => setScope(s)}
              className={`rounded-md px-2.5 py-1 font-medium ${scope === s ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              {s === 'academy' ? t('admin:help.history.wholeAcademy', 'Whole team') : t('admin:help.history.mine', 'Mine')}
            </button>
          ))}
        </div>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {!rows && !error && <p className="text-sm text-gray-400">{t('common:actions.loading', 'Loading…')}</p>}
      {rows && rows.length === 0 && (
        <p className="text-sm text-gray-500">{t('admin:help.history.empty', 'No questions yet. Anything you ask in the Ask tab is saved here.')}</p>
      )}
      {rows && rows.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white divide-y divide-gray-100 overflow-hidden">
          {rows.map((r) => (
            <div key={r.id}>
              <button type="button" onClick={() => setOpenId(openId === r.id ? null : r.id)} className="w-full text-left px-3 py-2.5 hover:bg-gray-50">
                <p className="text-sm text-gray-900 line-clamp-2">{r.question}</p>
                <p className="mt-0.5 flex items-center gap-2 text-[11px] text-gray-400">
                  <span>{fmtDate(r.createdAt, lang)}</span>
                  {scope === 'academy' && r.userName && <span>· {r.userName}</span>}
                  {r.rating === 'up' && <HandThumbUpIcon className="h-3.5 w-3.5 text-green-600" />}
                  {r.rating === 'down' && <HandThumbDownIcon className="h-3.5 w-3.5 text-red-600" />}
                  {r.escalated && <LifebuoyIcon className="h-3.5 w-3.5 text-brand-600" title={t('admin:help.chat.escalated', 'Sent to IAP support')} />}
                </p>
              </button>
              {openId === r.id && (
                <div className="px-3 pb-3 space-y-2">
                  <div className="rounded-xl bg-gray-50 px-3 py-2.5"><HelpMarkdown text={r.answer} onNavigate={onNavigate} /></div>
                  {(r.articleIds || []).filter(titleOf).length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {r.articleIds.filter(titleOf).map((id) => (
                        <button key={id} type="button" onClick={() => openArticle(id)} className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-2.5 py-1 text-xs text-gray-700 hover:border-brand-400">
                          <BookOpenIcon className="h-3.5 w-3.5" />{titleOf(id)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
